/**
 * Tarefas de uma fase — a UNIDADE gerenciada (Modelo B).
 * Linha 1: status, título, prioridade, badges, ações.
 * Linha 2 (à vista): responsável, horas (realizado/estimado), datas.
 * Painel expansível: descrição, checklist, anexos, comentários (com @menção).
 * Predecessoras (tarefa->tarefa, cross-fase) no ícone de ramo.
 */
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Plus, X, Circle, Loader, CheckCircle2, GitBranch, User, Clock, Calendar,
  MessageSquare, Paperclip, ListChecks, ChevronDown, ChevronUp, Trash2, Download, Send,
} from 'lucide-react';
import { tarefasAPI } from '../../../services/inovacaoAPI';
import { authAPI } from '../../../services/api';
import { mensagemErro } from '../labels';

const PROX = { a_fazer: 'fazendo', fazendo: 'feito', feito: 'a_fazer' };
const META = {
  a_fazer: { label: 'A fazer', cor: '#caa83e', Icon: Circle,       bg: '#fdf9ec', borda: '#ecdcab' }, // palha
  fazendo: { label: 'Fazendo', cor: '#3498db', Icon: Loader,       bg: '#eef6fc', borda: '#bcdcf2' }, // azul claro
  feito:   { label: 'Feito',   cor: '#27ae60', Icon: CheckCircle2, bg: '#eafaf1', borda: '#aee2c5' }, // verde derivado
};
const PRIO = {
  baixa:   { label: 'Baixa',   cor: '#7f8c8d', bg: '#f4f6f6' },
  media:   { label: 'Média',   cor: '#2980b9', bg: '#eaf2fb' },
  alta:    { label: 'Alta',    cor: '#e67e22', bg: '#fdf1e3' },
  urgente: { label: 'Urgente', cor: '#c0392b', bg: '#fdecea' },
};

const campoBase = { padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.8rem', fontFamily: 'inherit' };
const respStyle = (resp) => ({
  ...campoBase, maxWidth: 150, cursor: 'pointer',
  border: resp ? '1px solid #ddd' : '1px solid #f0c089',
  color: resp ? '#2c3e50' : '#b9772a', background: resp ? '#fff' : '#fff8ef',
});

export default function TarefasFase({ faseId, projetoId }) {
  const [tarefas, setTarefas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [nova, setNova] = useState({ titulo: '', responsavel_id: '', horas: '', inicio: '', fim: '' });
  const [loading, setLoading] = useState(true);
  const [deps, setDeps] = useState(null);
  const [aberta, setAberta] = useState(null);   // id da tarefa expandida
  const setN = (campo, v) => setNova((n) => ({ ...n, [campo]: v }));

  async function carregar() {
    try { setTarefas(await tarefasAPI.listar(faseId)); }
    catch { /* silencioso */ }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [faseId]);
  useEffect(() => { authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {}); }, []);

  const aplicar = (upd) => setTarefas((ts) => ts.map((x) => (x.id === upd.id ? upd : x)));
  const ehJustificativa = (e) => e?.response?.data?.detail?.code === 'JUSTIFICATIVA_OBRIGATORIA';

  async function salvar(t, body) {
    try { aplicar(await tarefasAPI.atualizar(t.id, body)); }
    catch (e) {
      if (ehJustificativa(e)) {
        const j = window.prompt('O plano já foi aprovado. Aumentar horas é um desvio. Justifique:');
        if (!j || !j.trim()) { toast.error('Justificativa obrigatória'); carregar(); return; }
        try { aplicar(await tarefasAPI.atualizar(t.id, { ...body, justificativa: j.trim() })); }
        catch (e2) { toast.error(mensagemErro(e2, 'Erro ao salvar')); carregar(); }
        return;
      }
      toast.error(mensagemErro(e, 'Erro ao salvar')); carregar();
    }
  }
  async function adicionar(e) {
    e.preventDefault();
    if (!nova.titulo.trim()) return;
    const base = {
      titulo: nova.titulo.trim(),
      responsavel_id: nova.responsavel_id ? parseInt(nova.responsavel_id) : null,
      horas_estimadas: nova.horas ? parseFloat(nova.horas) : 0,
      data_inicio_planejada: nova.inicio || null,
      data_fim_planejada: nova.fim || null,
    };
    const reset = () => setNova({ titulo: '', responsavel_id: '', horas: '', inicio: '', fim: '' });
    try {
      await tarefasAPI.criar(faseId, base);
      reset(); carregar();
    } catch (err) {
      if (ehJustificativa(err)) {
        const j = window.prompt('O plano já foi aprovado. Esta tarefa é um desvio. Justifique:');
        if (!j || !j.trim()) { toast.error('Justificativa obrigatória'); return; }
        try { await tarefasAPI.criar(faseId, { ...base, justificativa: j.trim() }); reset(); carregar(); }
        catch (e2) { toast.error(mensagemErro(e2, 'Erro ao criar tarefa')); }
        return;
      }
      toast.error(mensagemErro(err, 'Erro ao criar tarefa'));
    }
  }
  async function remover(t) {
    try { await tarefasAPI.deletar(t.id); setTarefas((ts) => ts.filter((x) => x.id !== t.id)); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }

  const feitas = tarefas.filter((t) => t.estado === 'feito').length;
  const horasEst = tarefas.reduce((s, t) => s + parseFloat(t.horas_estimadas || 0), 0);

  return (
    <div>
      <h4 style={h4Style}>
        Tarefas {tarefas.length > 0 && (
          <span style={{ color: '#95a5a6', fontWeight: 400 }}>
            · {feitas}/{tarefas.length} feitas{horasEst > 0 ? ` · ${horasEst}h previstas` : ''}
          </span>
        )}
      </h4>

      <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
        {tarefas.map((t) => {
          const m = META[t.estado] || META.a_fazer;
          const p = PRIO[t.prioridade] || PRIO.media;
          const expandida = aberta === t.id;
          return (
            <div key={t.id} style={{ border: `1px solid ${m.borda}`, borderLeft: `3px solid ${m.cor}`,
                                      borderRadius: 8, padding: '8px 10px', background: m.bg }}>
              {/* Linha 1: estado + título + prioridade + badges + ações */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" onClick={() => salvar(t, { estado: PROX[t.estado] })} title="Mudar status"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${m.cor}`,
                                 color: m.cor, background: '#fff', borderRadius: 12, padding: '2px 8px',
                                 fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  <m.Icon size={12} /> {m.label}
                </button>
                <input defaultValue={t.titulo} key={`tit-${t.id}`}
                       onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== t.titulo) salvar(t, { titulo: v }); }}
                       style={{ flex: 1, minWidth: 80, border: 'none', background: 'transparent', fontSize: '0.88rem', outline: 'none',
                                color: t.estado === 'feito' ? '#95a5a6' : '#2c3e50',
                                textDecoration: t.estado === 'feito' ? 'line-through' : 'none' }} />

                {/* prioridade como pílula colorida (select disfarçado) */}
                <select value={t.prioridade || 'media'} title="Prioridade"
                        onChange={(e) => salvar(t, { prioridade: e.target.value })}
                        style={{ border: `1px solid ${p.cor}`, color: p.cor, background: p.bg, borderRadius: 12,
                                 padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 600 }}>
                  {Object.entries(PRIO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>

                {t.eh_desvio && (
                  <span title={t.justificativa_desvio || 'Desvio do plano aprovado'}
                        style={{ background: '#fdf1e3', color: '#e67e22', border: '1px solid #f0c089',
                                 borderRadius: 10, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700,
                                 whiteSpace: 'nowrap' }}>
                    ⚠ desvio
                  </span>
                )}

                {/* badges de contadores */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#95a5a6', fontSize: '0.72rem' }}>
                  {t.checklist_total > 0 && (
                    <span title="Checklist" style={{ display: 'inline-flex', alignItems: 'center', gap: 2,
                          color: t.checklist_feitos === t.checklist_total ? '#27ae60' : '#95a5a6' }}>
                      <ListChecks size={13} />{t.checklist_feitos}/{t.checklist_total}
                    </span>
                  )}
                  {t.n_comentarios > 0 && <span title="Comentários" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MessageSquare size={13} />{t.n_comentarios}</span>}
                  {t.n_anexos > 0 && <span title="Anexos" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Paperclip size={13} />{t.n_anexos}</span>}
                </div>

                <button type="button" onClick={() => setDeps(t)} title="Predecessoras"
                        style={{ background: 'none', border: 'none', color: t.predecessoras?.length ? '#8e44ad' : '#bdc3c7', cursor: 'pointer', padding: 2 }}>
                  <GitBranch size={14} />
                </button>
                <button type="button" onClick={() => setAberta(expandida ? null : t.id)} title="Detalhes"
                        style={{ background: 'none', border: 'none', color: expandida ? 'var(--brand, #20a490)' : '#95a5a6', cursor: 'pointer', padding: 2 }}>
                  {expandida ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <button type="button" onClick={() => remover(t)} title="Remover"
                        style={{ background: 'none', border: 'none', color: '#bdc3c7', cursor: 'pointer', padding: 2 }}>
                  <X size={15} />
                </button>
              </div>

              {/* Linha 2: campos à vista — responsável, horas (realizado/estimado), datas */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                <select value={t.responsavel?.id || ''} title="Responsável"
                        onChange={(e) => salvar(t, { responsavel_id: e.target.value ? parseInt(e.target.value) : null })}
                        style={respStyle(t.responsavel)}>
                  <option value="">Responsável</option>
                  {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
                </select>

                <span title="Horas previstas (estimativa)" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#7f8c8d', fontSize: '0.8rem' }}>
                  <Clock size={14} style={{ color: '#95a5a6' }} />
                  <span style={{ fontSize: '0.68rem', color: '#95a5a6' }}>prev.</span>
                  <input type="number" step="0.5" min="0" placeholder="0" defaultValue={parseFloat(t.horas_estimadas) || ''} key={`he-${t.id}`}
                         title="Horas previstas (estimativa)"
                         onBlur={(e) => { const v = e.target.value; salvar(t, { horas_estimadas: v ? parseFloat(v) : 0 }); }}
                         style={{ ...campoBase, width: 48, textAlign: 'right' }} />
                  <span>h</span>
                </span>

                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Calendar size={14} style={{ color: '#95a5a6' }} />
                  <input type="date" value={t.data_inicio_planejada || ''} title="Início"
                         onChange={(e) => salvar(t, { data_inicio_planejada: e.target.value || null })} style={campoBase} />
                  <span style={{ color: '#cbd5d8' }}>→</span>
                  <input type="date" value={t.data_fim_planejada || ''} title="Fim"
                         onChange={(e) => salvar(t, { data_fim_planejada: e.target.value || null })} style={campoBase} />
                </span>
              </div>

              {t.predecessoras && t.predecessoras.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#8e44ad', marginTop: 6 }}>
                  ↳ depois de: {t.predecessoras.map((pr) => pr.titulo).join(', ')}
                </div>
              )}

              {expandida && (
                <DetalheTarefa tarefa={t} usuarios={usuarios} salvar={salvar} onMudou={carregar} />
              )}
            </div>
          );
        })}
        {!loading && tarefas.length === 0 && (
          <span style={{ color: '#bdc3c7', fontSize: '0.85rem' }}>Sem tarefas. Liste o que precisa ser feito para concluir a fase.</span>
        )}
      </div>

      <form onSubmit={adicionar}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                     border: '1px solid #d7e0e4', borderRadius: 10, padding: '8px 12px', background: '#fbfdfe' }}>
        <Circle size={16} style={{ color: '#cbd5d8', flexShrink: 0 }} />
        <input value={nova.titulo} onChange={(e) => setN('titulo', e.target.value)} placeholder="Adicionar tarefa…"
               style={{ flex: '1 1 160px', minWidth: 120, border: 'none', background: 'transparent', outline: 'none', fontSize: '0.9rem' }} />

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <User size={14} style={{ color: '#95a5a6', flexShrink: 0 }} />
          <select value={nova.responsavel_id} onChange={(e) => setN('responsavel_id', e.target.value)} title="Responsável"
                  style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.82rem',
                           color: nova.responsavel_id ? '#2c3e50' : '#95a5a6', cursor: 'pointer', maxWidth: 130 }}>
            <option value="">Sem responsável</option>
            {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
          </select>
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Horas previstas (estimativa)">
          <Clock size={14} style={{ color: '#95a5a6', flexShrink: 0 }} />
          <span style={{ color: '#95a5a6', fontSize: '0.7rem' }}>prev.</span>
          <input type="number" step="0.5" min="0" value={nova.horas} onChange={(e) => setN('horas', e.target.value)} placeholder="0" title="Horas previstas (estimativa)"
                 style={{ width: 40, border: 'none', background: 'transparent', outline: 'none', fontSize: '0.82rem', textAlign: 'right' }} />
          <span style={{ color: '#95a5a6', fontSize: '0.82rem' }}>h</span>
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={14} style={{ color: '#95a5a6', flexShrink: 0 }} />
          <input type="date" value={nova.inicio} onChange={(e) => setN('inicio', e.target.value)} title="Início"
                 style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.8rem', color: '#7f8c8d' }} />
          <span style={{ color: '#cbd5d8' }}>→</span>
          <input type="date" value={nova.fim} onChange={(e) => setN('fim', e.target.value)} title="Fim"
                 style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.8rem', color: '#7f8c8d' }} />
        </span>

        <button type="submit" title="Adicionar"
                style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px',
                         cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, whiteSpace: 'nowrap' }}>
          <Plus size={15} /> Adicionar
        </button>
      </form>

      {deps && (
        <ModalDeps tarefa={deps} projetoId={projetoId} onClose={() => setDeps(null)} onChange={carregar} />
      )}
    </div>
  );
}

/* ============== Painel de detalhe (descrição + checklist + anexos + comentários) ============== */

function DetalheTarefa({ tarefa, usuarios, salvar, onMudou }) {
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #d7e0e4', display: 'grid', gap: 14 }}>
      <Descricao tarefa={tarefa} salvar={salvar} />
      <Checklist tarefaId={tarefa.id} onMudou={onMudou} />
      <Anexos tarefaId={tarefa.id} onMudou={onMudou} />
      <Comentarios tarefaId={tarefa.id} usuarios={usuarios} onMudou={onMudou} />
    </div>
  );
}

const secaoTitulo = { fontSize: '0.7rem', fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };

function Descricao({ tarefa, salvar }) {
  return (
    <div>
      <div style={secaoTitulo}>Descrição / definição de pronto</div>
      <textarea defaultValue={tarefa.descricao || ''} key={`desc-${tarefa.id}`} rows={2}
                placeholder="O que precisa ser feito? Quando consideramos concluída?"
                onBlur={(e) => { const v = e.target.value; if (v !== (tarefa.descricao || '')) salvar(tarefa, { descricao: v || null }); }}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e1e8eb', borderRadius: 6,
                         padding: 8, fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical', background: '#fff' }} />
    </div>
  );
}

function Checklist({ tarefaId, onMudou }) {
  const [itens, setItens] = useState([]);
  const [novo, setNovo] = useState('');
  useEffect(() => { tarefasAPI.listarChecklist(tarefaId).then(setItens).catch(() => {}); }, [tarefaId]);

  async function add(e) {
    e.preventDefault();
    if (!novo.trim()) return;
    try { const it = await tarefasAPI.criarChecklist(tarefaId, novo.trim()); setItens((l) => [...l, it]); setNovo(''); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }
  async function toggle(it) {
    try { const upd = await tarefasAPI.atualizarChecklist(it.id, { concluido: !it.concluido });
          setItens((l) => l.map((x) => (x.id === it.id ? upd : x))); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }
  async function rm(it) {
    try { await tarefasAPI.removerChecklist(it.id); setItens((l) => l.filter((x) => x.id !== it.id)); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }
  const feitos = itens.filter((i) => i.concluido).length;

  return (
    <div>
      <div style={secaoTitulo}>Checklist {itens.length > 0 && <span style={{ fontWeight: 400 }}>· {feitos}/{itens.length}</span>}</div>
      <div style={{ display: 'grid', gap: 4 }}>
        {itens.map((it) => (
          <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={it.concluido} onChange={() => toggle(it)} style={{ cursor: 'pointer' }} />
            <span style={{ flex: 1, fontSize: '0.85rem', color: it.concluido ? '#95a5a6' : '#2c3e50',
                           textDecoration: it.concluido ? 'line-through' : 'none' }}>{it.texto}</span>
            <button type="button" onClick={() => rm(it)} title="Remover"
                    style={{ background: 'none', border: 'none', color: '#cbd5d8', cursor: 'pointer', padding: 0 }}><X size={13} /></button>
          </div>
        ))}
      </div>
      <form onSubmit={add} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input value={novo} onChange={(e) => setNovo(e.target.value)} placeholder="+ item do checklist"
               style={{ flex: 1, border: '1px solid #e1e8eb', borderRadius: 6, padding: '5px 8px', fontSize: '0.83rem' }} />
      </form>
    </div>
  );
}

function Anexos({ tarefaId, onMudou }) {
  const [itens, setItens] = useState([]);
  const [subindo, setSubindo] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { tarefasAPI.listarAnexos(tarefaId).then(setItens).catch(() => {}); }, [tarefaId]);

  async function subir(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubindo(true);
    try { const a = await tarefasAPI.subirAnexo(tarefaId, file); setItens((l) => [a, ...l]); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro ao subir arquivo')); }
    finally { setSubindo(false); if (inputRef.current) inputRef.current.value = ''; }
  }
  async function baixar(a) {
    try {
      const resp = await tarefasAPI.baixarAnexo(a.id);
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url; link.download = a.nome_original; link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(mensagemErro(err, 'Erro ao baixar')); }
  }
  async function rm(a) {
    try { await tarefasAPI.removerAnexo(a.id); setItens((l) => l.filter((x) => x.id !== a.id)); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }
  const fmt = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div>
      <div style={secaoTitulo}>Anexos</div>
      <div style={{ display: 'grid', gap: 4 }}>
        {itens.map((a) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.83rem' }}>
            <Paperclip size={13} style={{ color: '#95a5a6', flexShrink: 0 }} />
            <button type="button" onClick={() => baixar(a)} title="Baixar"
                    style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', color: '#2980b9', cursor: 'pointer', padding: 0,
                             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome_original}</button>
            <span style={{ color: '#bdc3c7', flexShrink: 0 }}>{fmt(a.tamanho_bytes)}</span>
            <button type="button" onClick={() => baixar(a)} title="Baixar"
                    style={{ background: 'none', border: 'none', color: '#95a5a6', cursor: 'pointer', padding: 0 }}><Download size={13} /></button>
            <button type="button" onClick={() => rm(a)} title="Remover"
                    style={{ background: 'none', border: 'none', color: '#cbd5d8', cursor: 'pointer', padding: 0 }}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" onChange={subir} style={{ display: 'none' }} id={`anx-${tarefaId}`} />
      <label htmlFor={`anx-${tarefaId}`}
             style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer',
                      color: 'var(--brand, #20a490)', fontSize: '0.82rem', fontWeight: 600 }}>
        <Plus size={14} /> {subindo ? 'Enviando…' : 'Anexar arquivo'}
      </label>
    </div>
  );
}

function Comentarios({ tarefaId, usuarios, onMudou }) {
  const [coms, setComs] = useState([]);
  const [texto, setTexto] = useState('');
  const [mencoes, setMencoes] = useState([]);     // ids citados
  const [sugestao, setSugestao] = useState(null); // {termo} ou null
  const taRef = useRef(null);

  useEffect(() => { tarefasAPI.listarComentarios(tarefaId).then(setComs).catch(() => {}); }, [tarefaId]);

  function onChange(e) {
    const v = e.target.value;
    setTexto(v);
    // detecta "@termo" no fim do que está sendo digitado
    const m = v.slice(0, e.target.selectionStart).match(/@(\w*)$/);
    setSugestao(m ? { termo: m[1].toLowerCase() } : null);
  }
  function escolher(u) {
    // troca o "@termo" parcial pelo nome
    const primeiro = (u.nome_completo || '').split(' ')[0];
    setTexto((t) => t.replace(/@(\w*)$/, `@${primeiro} `));
    setMencoes((l) => (l.includes(u.id) ? l : [...l, u.id]));
    setSugestao(null);
    taRef.current?.focus();
  }
  async function enviar(e) {
    e.preventDefault();
    if (!texto.trim()) return;
    // só mantém menções cujo primeiro nome ainda aparece no texto
    const validos = mencoes.filter((id) => {
      const u = usuarios.find((x) => x.id === id);
      return u && texto.includes(`@${(u.nome_completo || '').split(' ')[0]}`);
    });
    try {
      const c = await tarefasAPI.criarComentario(tarefaId, texto.trim(), validos);
      setComs((l) => [...l, c]); setTexto(''); setMencoes([]); onMudou();
    } catch (err) { toast.error(mensagemErro(err, 'Erro ao comentar')); }
  }
  async function rm(c) {
    try { await tarefasAPI.removerComentario(c.id); setComs((l) => l.filter((x) => x.id !== c.id)); onMudou(); }
    catch (err) { toast.error(mensagemErro(err, 'Erro')); }
  }
  const filtradas = sugestao
    ? usuarios.filter((u) => (u.nome_completo || '').toLowerCase().includes(sugestao.termo)).slice(0, 6)
    : [];

  return (
    <div>
      <div style={secaoTitulo}>Comentários</div>
      <div style={{ display: 'grid', gap: 8, marginBottom: 8 }}>
        {coms.map((c) => (
          <div key={c.id} style={{ background: '#fff', border: '1px solid #eef2f4', borderRadius: 6, padding: '6px 9px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: '#7f8c8d', marginBottom: 2 }}>
              <strong style={{ color: '#2c3e50' }}>{c.autor?.nome || 'Usuário'}</strong>
              <span>· {new Date(c.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
              {c.editado_em && <span style={{ fontStyle: 'italic' }}>(editado)</span>}
              {c.pode_editar && (
                <button type="button" onClick={() => rm(c)} title="Excluir"
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#cbd5d8', cursor: 'pointer', padding: 0 }}><X size={13} /></button>
              )}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#2c3e50', whiteSpace: 'pre-wrap' }}>{realceMencoes(c.texto)}</div>
          </div>
        ))}
        {coms.length === 0 && <span style={{ color: '#bdc3c7', fontSize: '0.82rem' }}>Sem comentários ainda.</span>}
      </div>
      <form onSubmit={enviar} style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea ref={taRef} value={texto} onChange={onChange} rows={2}
                    placeholder="Escreva um comentário… use @ para citar alguém"
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) enviar(e); }}
                    style={{ flex: 1, border: '1px solid #e1e8eb', borderRadius: 6, padding: 8, fontSize: '0.85rem',
                             fontFamily: 'inherit', resize: 'vertical', background: '#fff' }} />
          <button type="submit" title="Comentar (Ctrl+Enter)"
                  style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none', borderRadius: 7, padding: '9px 12px',
                           cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><Send size={15} /></button>
        </div>
        {sugestao && filtradas.length > 0 && (
          <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, background: '#fff',
                        border: '1px solid #d7e0e4', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                        zIndex: 20, minWidth: 200, overflow: 'hidden' }}>
            {filtradas.map((u) => (
              <button type="button" key={u.id} onClick={() => escolher(u)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                               padding: '7px 10px', cursor: 'pointer', fontSize: '0.84rem', color: '#2c3e50' }}
                      onMouseDown={(e) => e.preventDefault()}>
                {u.nome_completo}
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
}

// realça @primeironome no texto do comentário
function realceMencoes(texto) {
  const partes = String(texto).split(/(@\w+)/g);
  return partes.map((p, i) =>
    p.startsWith('@')
      ? <span key={i} style={{ color: '#2980b9', fontWeight: 600 }}>{p}</span>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

/* ============== Modal de predecessoras (inalterado) ============== */

function ModalDeps({ tarefa, projetoId, onClose, onChange }) {
  const [opcoes, setOpcoes] = useState([]);
  const [preds, setPreds] = useState(tarefa.predecessoras || []);
  const [sel, setSel] = useState('');

  useEffect(() => {
    if (projetoId) tarefasAPI.doProjeto(projetoId).then(setOpcoes).catch(() => {});
  }, [projetoId]);

  async function add() {
    if (!sel) return;
    try {
      const upd = await tarefasAPI.addPredecessora(tarefa.id, parseInt(sel));
      setPreds(upd.predecessoras); setSel(''); onChange();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao adicionar predecessora')); }
  }
  async function rm(pid) {
    try { await tarefasAPI.removePredecessora(tarefa.id, pid); setPreds(preds.filter((p) => p.id !== pid)); onChange(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro')); }
  }
  const disponiveis = opcoes.filter((o) => o.id !== tarefa.id && !preds.some((p) => p.id === o.id));

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={box} onMouseDown={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', color: '#2c3e50' }}>Predecessoras</h3>
        <p style={{ margin: '0 0 14px', color: '#7f8c8d', fontSize: '0.85rem' }}>
          "{tarefa.titulo}" só deve começar depois que estas tarefas terminarem.
        </p>
        {preds.length === 0 ? (
          <div style={{ color: '#bdc3c7', fontSize: '0.85rem', marginBottom: 12 }}>Nenhuma predecessora.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {preds.map((p) => (
              <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f4ecf7',
                                        color: '#8e44ad', borderRadius: 12, padding: '3px 10px', fontSize: '0.8rem' }}>
                {p.titulo}
                <button onClick={() => rm(p.id)} title="Remover"
                        style={{ background: 'none', border: 'none', color: '#8e44ad', cursor: 'pointer', padding: 0, display: 'inline-flex' }}>
                  <X size={13} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={sel} onChange={(e) => setSel(e.target.value)}
                  style={{ flex: 1, padding: 8, borderRadius: 4, border: '1px solid #ccc', fontSize: '0.85rem' }}>
            <option value="">Escolha a tarefa</option>
            {disponiveis.map((o) => <option key={o.id} value={o.id}>{o.fase_nome} · {o.titulo}</option>)}
          </select>
          <button type="button" onClick={add} disabled={!sel}
                  style={{ background: sel ? 'var(--brand, #20a490)' : '#bdc3c7', color: '#fff', border: 'none', borderRadius: 4,
                           padding: '0 14px', cursor: sel ? 'pointer' : 'not-allowed' }}>
            Adicionar
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose}
                  style={{ background: '#ecf0f1', color: '#555', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,30,40,0.45)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const box = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460,
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)' };
const h4Style = { margin: '0 0 8px', fontSize: '0.85rem', color: '#2c3e50', textTransform: 'uppercase', letterSpacing: 0.5 };
